// GenToken.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './GenToken.css';
import { validateTokenForm, isValidPublicKey } from './GenToken/validation';
import { displayNetworkName } from './GenToken/solanaConfig';
import { truncateAddress } from './GenToken/tokenUtils';
import RegisterMetadata from './GenToken/RegisterMetadata';
import ListTokens from './GenToken/ListTokens';
import ExternalWalletTransactionHandler from './GenToken/ExternalWalletTransactionHandler';

const GenToken = ({ language, user }) => {
  const [tokenName, setTokenName] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [tokenDecimals, setTokenDecimals] = useState(9);
  const [tokenSupply, setTokenSupply] = useState(1000000000);
  const [network, setNetwork] = useState('devnet');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [userWallet, setUserWallet] = useState('');
  const [transactions, setTransactions] = useState([]);

  // 지갑 생성 관련 state 추가
  const [walletSeedPhrase, setWalletSeedPhrase] = useState('');
  const [showRecoveryPhrase, setShowRecoveryPhrase] = useState(false);
  const [seedPhraseConfirmed, setSeedPhraseConfirmed] = useState(false);
  const [seedPhraseBackedUp, setSeedPhraseBackedUp] = useState(false);
  const [showImportWallet, setShowImportWallet] = useState(false);
  const [importedWalletAddress, setImportedWalletAddress] = useState('');
  const [walletAddressError, setWalletAddressError] = useState('');
  const [useExistingWallet, setUseExistingWallet] = useState(false);

  const [showMetadataModal, setShowMetadataModal] = useState(false);

  // 외부 지갑 트랜잭션 처리를 위한 state 추가
  const [showWalletHandler, setShowWalletHandler] = useState(false);
  const [pendingTokenData, setPendingTokenData] = useState(null);

  const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001'; 

  // Fetch user's wallet and previous transactions on component mount
  useEffect(() => {
    if (user && user.uid) {
      fetchUserWallet();
      fetchTransactions();
    }
  }, [user]);

  const fetchUserWallet = async () => {
    try {
      const response = await axios.get(`${SERVER_ROOT}/api/solana/wallet/${user.uid}`);
      if (response.data && response.data.publicKey) {
        setUserWallet(response.data.publicKey);
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      if (!user || !user.uid) return;
      
      const response = await axios.get(`${SERVER_ROOT}/api/solana/transactions/${user.uid}`);
      if (response.data) {
        setTransactions(response.data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  // 지갑 생성 (니모닉 생성 단계)
  const createWallet = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // 서버에 니모닉 생성 요청 (서버는 생성만 하고 저장하지 않음)
      const response = await axios.get(`${SERVER_ROOT}/api/solana/wallet/generate-mnemonic`);
      
      if (response.data && response.data.mnemonic) {
        setWalletSeedPhrase(response.data.mnemonic);
        setShowRecoveryPhrase(true);
        
        // 이 시점에서는 아직 지갑이 등록되지 않음
        // 사용자가 복구 문구를 백업한 후에만 다음 단계로 진행
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to generate recovery phrase');
    } finally {
      setIsLoading(false);
    }
  };

  // 복구 문구 백업 확인 후 지갑 등록
  const confirmSeedPhraseBackup = async () => {
    try {
      setIsLoading(true);
      
      // 니모닉으로부터 public key 생성
      const publicKey = await derivePublicKeyFromMnemonic(walletSeedPhrase);
      
      // 서버에 public key만 등록
      const response = await axios.post(`${SERVER_ROOT}/api/solana/wallet/register`, {
        userId: user.uid,
        userEmail: user.email,
        publicKey: publicKey
      });
      
      if (response.data && response.data.success) {
        setUserWallet(publicKey);
        setSeedPhraseBackedUp(true);
        setShowRecoveryPhrase(false);
        
        alert(language === 'KR'
          ? '지갑이 성공적으로 생성되었습니다. 복구 문구는 안전하게 보관하세요!'
          : 'Wallet successfully created. Keep your recovery phrase safe!');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to register wallet');
    } finally {
      setIsLoading(false);
    }
  };

  // 니모닉으로부터 public key 생성 (실제 구현은 클라이언트 측 라이브러리에 따라 다름)
  const derivePublicKeyFromMnemonic = async (mnemonic) => {
    try {
      // 서버에 요청하여 니모닉으로부터 공개키 유도
      const response = await axios.post(`${SERVER_ROOT}/api/solana/derive-pubkey`, {
        mnemonic: mnemonic
      });
      
      if (response.data && response.data.publicKey) {
        return response.data.publicKey;
      }
      
      throw new Error('Failed to derive public key from mnemonic');
    } catch (error) {
      console.error('Error deriving public key:', error);
      throw error;
    }
  };
  

  // 기존 지갑 가져오기
  const importExistingWallet = async () => {
    // 지갑 주소 유효성 검증
    if (!importedWalletAddress || !isValidPublicKey(importedWalletAddress)) {
      setWalletAddressError(language === 'KR'
        ? '유효한 Solana 지갑 주소를 입력해주세요.'
        : 'Please enter a valid Solana wallet address.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setWalletAddressError('');
      
      const response = await axios.post(`${SERVER_ROOT}/api/solana/wallet/import`, {
        userId: user.uid,
        userEmail: user.email,
        publicKey: importedWalletAddress,
        useExistingWallet: true
      });
      
      if (response.data && response.data.publicKey) {
        setUserWallet(response.data.publicKey);
        setShowImportWallet(false);
        alert(language === 'KR' 
          ? '지갑이 성공적으로 등록되었습니다.' 
          : 'Wallet imported successfully.');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to import wallet');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();
    
    // Validate form data
    const validationError = validateTokenForm({
      tokenName,
      tokenSymbol,
      tokenDecimals,
      tokenSupply,
      network
    });
    
    if (validationError) {
      setError(validationError);
      return;
    }
    
    // Check if user is logged in
    if (!user || !user.uid) {
      setError(language === 'KR' 
        ? '토큰을 생성하려면 로그인이 필요합니다.' 
        : 'Login required to create tokens.');
      return;
    }
    
    // Check if user has a wallet
    if (!userWallet) {
      setError(language === 'KR' 
        ? '토큰을 생성하려면 먼저 지갑을 생성해야 합니다.' 
        : 'You need to create a wallet first.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setResult(null);
    
    try {
      // userWallet 값과 useExistingWallet 상태를 기반으로 외부 지갑 사용 여부 결정
      // userWallet이 있고, 외부에서 임포트된 지갑인지 확인
      // 주의: 여기서는 간단한 예시로, 실제 구현은 앱의 상태 관리 방식에 따라 달라질 수 있습니다
      
      // 지갑 타입 확인 로직
      let useExternal = false;
      
      // 1. DB에서 가져온 지갑 정보가 있다면, 그것을 사용
      if (userWallet) {
        // 지갑 주소 형식이나 다른 정보로 외부 지갑인지 판단
        // 예: 특정 접두사가 있거나 길이가 특정 값이거나
        // 또는 userWallet 외에 다른 상태 변수로 이미 외부 지갑인지 여부를 저장해두었다면 그것을 사용
        
        // 임시 예시: 이전에 fetchUserWallet을 통해 가져온 정보가 있다면
        // 실제 구현에서는 이 부분을 앱의 상태에 맞게 수정해야 합니다
        console.log('Using wallet:', userWallet);
        
        // 만약 사용자가 '외부 지갑 사용' 체크박스를 선택했다면
        useExternal = useExistingWallet; // useState로 관리하는 체크박스 상태 사용
      }
      
      console.log('Wallet connection status:', { 
        userWallet,
        isConnected: !!userWallet,
        useExistingWallet: useExternal
      });
      
      const response = await axios.post(`${SERVER_ROOT}/api/solana/token/create`, {
        userId: user.uid,
        tokenName,
        tokenSymbol,
        tokenDecimals: Number(tokenDecimals),
        tokenSupply: Number(tokenSupply),
        network,
        useExistingWallet: useExternal // 지갑 연결 상태에 따라 동적 설정
      });
      
      console.log('Token creation response:', response.data);

      // 서버 응답 후 로그 추가
      console.log('Token creation response full:', response);
      console.log('Token status:', response.data.status);
      console.log('Using external wallet handler:', response.data.status === 'pending');

      if (response.data.status === 'pending') {
        // 지갑 연결 및 트랜잭션 처리 컴포넌트 표시
        console.log('Setting pendingTokenData:', response.data);
        setPendingTokenData(response.data);
        console.log('Setting showWalletHandler to true');
        setShowWalletHandler(true);
        setIsLoading(false);  // 로딩 상태 해제
      }
      // 서버 지갑 흐름인 경우 (status 필드 없음)
      else {
        setResult(response.data);
        
        // 토큰 생성 성공 후 검증 단계 추가
        if (response.data.tokenAddress) {
          try {
            // 더 긴 대기 시간 추가 (5초)
            await new Promise(resolve => setTimeout(resolve, 5000));
            
            // 첫 번째 검증 시도
            let validationResponse = await axios.get(
              `${SERVER_ROOT}/api/solana/token/validate`, 
              { params: { tokenAddress: response.data.tokenAddress, network } }
            );
            
            // 토큰이 확인되지 않은 경우, 다시 시도
            if (!validationResponse.data.exists) {
              // 10초 더 기다리고 다시 시도
              await new Promise(resolve => setTimeout(resolve, 10000));
              validationResponse = await axios.get(
                `${SERVER_ROOT}/api/solana/token/validate`, 
                { params: { tokenAddress: response.data.tokenAddress, network } }
              );
            }
            
            if (!validationResponse.data.exists) {
              setError(language === 'KR'
                ? '토큰이 생성되었지만 블록체인에서 확인되지 않았습니다. 블록체인 동기화에 시간이 걸릴 수 있으니 나중에 다시 확인해주세요.'
                : 'Token was created but not confirmed on the blockchain yet. It may take time for the blockchain to sync, please check again later.');
            }
          } catch (validationError) {
            console.warn('Token validation warning:', validationError);
          }
        }
        
        fetchTransactions(); // Refresh transactions list
      }
    } catch (error) {
      console.error('Error creating token:', error);
      
      // 더 자세한 오류 메시지 표시
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create token';
      const errorDetails = error.response?.data?.details || '';
      setError(`${errorMessage}${errorDetails ? `: ${errorDetails}` : ''}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 4. 외부 지갑 트랜잭션 핸들러 관련 함수 추가
  const handleWalletSuccess = (tokenData) => {
    // 트랜잭션 성공 시 결과 표시
    setResult(tokenData);
    setTimeout(() => {
      setShowWalletHandler(false);
      setPendingTokenData(null);
      fetchTransactions(); // 트랜잭션 목록 새로고침
    }, 3000);
  };
  
  const handleWalletError = (errorMessage) => {
    // 에러 처리는 컴포넌트 내부에서 처리되므로 추가 액션 불필요
    console.error('Wallet transaction error:', errorMessage);
  };
  
  const handleWalletClose = () => {
    setShowWalletHandler(false);
    setPendingTokenData(null);
  };

  const handleNetworkChange = (e) => {
    setNetwork(e.target.value);
  };

  // 3. 메타데이터 등록 모달 열기/닫기 함수
  const toggleMetadataModal = () => {
    setShowMetadataModal(!showMetadataModal);
  };

  // 4. 메타데이터 등록 성공 핸들러
  const handleMetadataSuccess = (metadataResult) => {
    // 토큰 정보 업데이트 또는 필요한 작업 수행
    fetchTransactions(); // 트랜잭션 목록 새로고침
    setTimeout(() => {
      setShowMetadataModal(false);
    }, 2000);
  };

  // 2. 메타데이터 등록 핸들러 추가 (toggleMetadataModal 함수 근처)
  const handleRegisterMetadata = (tokenInfo) => {
    setResult({
      tokenAddress: tokenInfo.tokenAddress,
      tokenName: tokenInfo.tokenName,
      tokenSymbol: tokenInfo.tokenSymbol
    });
    setNetwork(tokenInfo.network || network);
    setShowMetadataModal(true);
  };

  const toggleImportWallet = () => {
    setShowImportWallet(!showImportWallet);
    if (!showImportWallet) {
      setImportedWalletAddress('');
      setWalletAddressError('');
    }
  };

  return (
    <div className="gen-token-container">
      <h2 className="gen-token-title">
        {language === 'KR' ? 'Solana 토큰 생성기' : 'Solana Token Generator'}
        <button onClick={() => window.open('/howTo_genToken.html', '_blank')} style={{marginLeft: '50px', width:'150px', fontSize: '14px'}}> 사용설명서</button>
      </h2>
      
      {showRecoveryPhrase ? (
        // 복구 문구 백업 UI
        <div className="recovery-phrase-container">
          <h3 className="important-title">
            {language === 'KR' ? '매우 중요: 복구 문구 백업' : 'VERY IMPORTANT: Backup Recovery Phrase'}
          </h3>
          <div className="warning-box">
            <p className="warning-text">
              {language === 'KR' 
                ? '아래 복구 문구는 지갑을 복구하는 유일한 방법입니다. 서버에는 저장되지 않습니다.' 
                : 'The recovery phrase below is the ONLY way to restore your wallet. It is NOT stored on the server.'}
            </p>
            <p className="warning-text">
              {language === 'KR' 
                ? '이 단어들을 순서대로 안전한 곳에 백업하세요. 이 문구를 분실하면 자산에 영구적으로 접근할 수 없게 됩니다.' 
                : 'Back up these words in order in a safe place. If you lose this phrase, you will permanently lose access to your assets.'}
            </p>
            <p className="warning-text highlight">
              {language === 'KR' 
                ? '이 문구를 스크린샷으로 저장하거나 이메일로 보내지 마세요. 안전하지 않습니다!' 
                : 'DO NOT take a screenshot or email these words. It\'s not secure!'}
            </p>
          </div>
          
          <div className="seed-phrase-box">
            <div className="seed-phrase">
              {walletSeedPhrase.split(' ').map((word, index) => (
                <span key={index} className="seed-word">
                  <span className="seed-number">{index + 1}.</span> {word}
                </span>
              ))}
            </div>
            <button 
              className="copy-button"
              onClick={() => {
                navigator.clipboard.writeText(walletSeedPhrase);
                alert(language === 'KR' ? '복사되었습니다!' : 'Copied!');
              }}
            >
              {language === 'KR' ? '복사' : 'Copy'}
            </button>
          </div>
          
          <div className="confirm-backup">
            <label className="checkbox-container">
              <input 
                type="checkbox" 
                checked={seedPhraseConfirmed} 
                onChange={() => setSeedPhraseConfirmed(!seedPhraseConfirmed)}
              />
              <span>
                {language === 'KR' 
                  ? '복구 문구를 안전한 곳에 백업했으며, 이것이 내 지갑을 복구할 수 있는 유일한 방법임을 이해합니다.' 
                  : 'I have backed up my recovery phrase in a safe place and understand this is the ONLY way to recover my wallet.'}
              </span>
            </label>
            
            <button 
              className="confirm-backup-btn"
              disabled={!seedPhraseConfirmed || isLoading}
              onClick={confirmSeedPhraseBackup}
            >
              {isLoading 
                ? (language === 'KR' ? '처리 중...' : 'Processing...') 
                : (language === 'KR' ? '백업 확인 및 지갑 생성' : 'Confirm Backup & Create Wallet')}
            </button>
          </div>
        </div>
      ) : !userWallet ? (
        // 지갑 생성/가져오기 옵션 UI
        <div className="wallet-section">
          <h3 className="step-title">
            {language === 'KR' ? '1단계: 지갑 생성 또는 연결' : 'Step 1: Create or Connect Wallet'}
          </h3>
          <p className="step-description">
            {language === 'KR' 
              ? '토큰을 생성하려면 Solana 지갑이 필요합니다. 새 지갑을 만들거나 기존 지갑을 연결하세요.' 
              : 'To create a token, you need a Solana wallet. You can create a new wallet or connect an existing one.'}
          </p>
          
          <div className="wallet-options">
            <button 
              className="create-wallet-btn" 
              onClick={createWallet}
              disabled={isLoading}
            >
              {language === 'KR' ? '새 지갑 생성하기' : 'Create New Wallet'}
            </button>
            
            <div className="wallet-option-divider">
              <span>{language === 'KR' ? '또는' : 'OR'}</span>
            </div>
            
            <button 
              className="import-wallet-btn" 
              onClick={toggleImportWallet}
              disabled={isLoading}
            >
              {language === 'KR' ? '기존 지갑 연결하기' : 'Connect Existing Wallet'}
            </button>
          </div>
          
          {showImportWallet && (
            <div className="import-wallet-section">
              <p className="import-wallet-description">
                {language === 'KR' 
                  ? '기존 Solana 지갑 주소를 입력하세요. 이 지갑이 토큰의 소유자가 됩니다.' 
                  : 'Enter your existing Solana wallet address. This wallet will be the owner of the token.'}
              </p>
              <div className="form-group">
                <label htmlFor="walletAddress">
                  {language === 'KR' ? '지갑 주소' : 'Wallet Address'}:
                </label>
                <input
                  type="text"
                  id="walletAddress"
                  value={importedWalletAddress}
                  onChange={(e) => setImportedWalletAddress(e.target.value)}
                  placeholder={language === 'KR' ? 'Solana 지갑 주소 입력' : 'Enter Solana wallet address'}
                  disabled={isLoading}
                />
                {walletAddressError && (
                  <div className="field-error">{walletAddressError}</div>
                )}
                <small className="field-help">
                  {language === 'KR' 
                    ? 'Solana 주소는 보통 44자리 문자열입니다.' 
                    : 'Solana addresses are 44 characters long.'}
                </small>
              </div>
              <div className="wallet-options-checkbox">
                <input
                  type="checkbox"
                  id="useExistingWallet"
                  checked={useExistingWallet}
                  onChange={() => setUseExistingWallet(!useExistingWallet)}
                />
                <label htmlFor="useExistingWallet">
                  {language === 'KR' 
                    ? '외부 지갑 사용하여 토큰 생성 트랜잭션 서명하기 (Phantom 또는 Solflare 지갑 필요)' 
                    : 'Use external wallet (Requires Phantom or Solflare wallet) (if unchecked, created by server)'}
                  {/* {language === 'KR' 
                    ? '이 지갑에서 토큰 생성 트랜잭션 서명하기' 
                    : 'Sign token creation transactions from this wallet (if unchecked, created by server)'} */} 
                </label>
                <button onClick={() => window.open('/explain_토큰생성트랜잭션서명.html', '_blank')} style={{marginLeft: '10px', width:'90px', fontSize: '14px'}}> 도움말</button>
                <button onClick={() => window.open('/howtophantom.html', '_blank')} style={{marginLeft: '10px', width:'90px', fontSize: '14px'}}> 도움말</button>
              </div>
              <div className="import-wallet-actions">
                <button 
                  className="import-wallet-confirm-btn" 
                  onClick={importExistingWallet}
                  disabled={isLoading || !importedWalletAddress}
                >
                  {isLoading
                    ? (language === 'KR' ? '처리 중...' : 'Processing...')
                    : (language === 'KR' ? '지갑 연결하기' : 'Connect Wallet')}
                </button>
                <button 
                  className="import-wallet-cancel-btn" 
                  onClick={toggleImportWallet}
                  disabled={isLoading}
                >
                  {language === 'KR' ? '취소' : 'Cancel'}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        // 지갑 연결 완료 UI
        <div className="wallet-info">
          <h3 className="step-title">
            {language === 'KR' ? '1단계: 지갑 연결 완료' : 'Step 1: Wallet Connected'}
          </h3>
          <p className="step-description">
            {language === 'KR' 
              ? '지갑이 성공적으로 연결되었습니다. 이제 토큰을 만들 수 있습니다.' 
              : 'Your wallet has been successfully created. You can now create your token.'}
          </p>
          <div className="wallet-address-container">
            <p>
              {language === 'KR' ? '지갑 주소: ' : 'Wallet Address: '}
              <span className="wallet-address">{truncateAddress(userWallet, 8, 8)}</span>
              <button 
                className="copy-button small"
                onClick={() => {
                  navigator.clipboard.writeText(userWallet);
                  alert(language === 'KR' ? '주소가 복사되었습니다!' : 'Address copied!');
                }}
              >
                {language === 'KR' ? '복사' : 'Copy'}
              </button>
            </p>
            <a 
              href={`https://explorer.solana.com/address/${userWallet}?cluster=${network}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="solana-explorer-link"
            >
              {language === 'KR' 
                ? 'Solana Explorer에서 지갑 보기' 
                : 'View wallet on Solana Explorer'}
            </a>
          </div>

          <div className="wallet-options-checkbox">
            <input
              type="checkbox"
              id="useExistingWallet"
              checked={useExistingWallet}
              onChange={() => setUseExistingWallet(!useExistingWallet)}
            />
            <label htmlFor="useExistingWallet">
              {language === 'KR' 
                ? '외부 지갑으로 토큰 생성 트랜잭션 서명하기' 
                : 'Use external wallet (Requires Phantom or Solflare wallet) (if unchecked, created by server)'}
              {/* {language === 'KR' 
                ? '이 지갑에서 토큰 생성 트랜잭션 서명하기' 
                : 'Sign token creation transactions from this wallet (if unchecked, created by server)'} */} 
            </label>
            <button onClick={() => window.open('/explain_토큰생성트랜잭션서명.html', '_blank')} style={{marginLeft: '10px', width:'100px', fontSize: '14px'}}> 알아보기</button>
            <button onClick={() => window.open('/howtophantom.html', '_blank')} style={{marginLeft: '10px', width:'130px', fontSize: '14px'}}> 지갑설치방법</button>

          </div>
        </div> 

        
      )}

      
      
      <div className="token-form-container">
        <h3 className="step-title">
          {language === 'KR' ? '2단계: 토큰 정보 입력' : 'Step 2: Enter Token Details'}
        </h3>
        <p className="step-description">
          {language === 'KR' 
            ? '발행하고자 하는 토큰에 대한 정보를 입력하세요. 각 필드는 토큰의 특성을 결정합니다.' 
            : 'Enter the details of your new token. Each field determines the characteristics of your token.'}
        </p>
        <form className="token-form" onSubmit={handleCreateToken}>
          <div className="form-group" style={{display: 'flex', flexDirection: 'row'}}>
            <label htmlFor="tokenName" style={{width: '200px', textAlign: 'center'}}>
              {language === 'KR' ? '토큰 이름' : 'Token Name'}:
            </label>
            <input
              type="text"
              id="tokenName"
              value={tokenName}
              onChange={(e) => setTokenName(e.target.value)}
              placeholder={language === 'KR' ? '예: My Awesome Token' : 'e.g., My Awesome Token'}
              disabled={isLoading || !userWallet}
            /> 
          </div>
          
          <div className="form-group" style={{display: 'flex', flexDirection: 'row'}}>
            <label htmlFor="tokenSymbol" style={{width: '200px', textAlign: 'center'}}>
              {language === 'KR' ? '토큰 기호' : 'Token Symbol'}:
            </label>
            <input
              type="text"
              id="tokenSymbol"
              value={tokenSymbol}
              onChange={(e) => setTokenSymbol(e.target.value.toUpperCase())}
              placeholder={language === 'KR' ? '토큰의 짧은 약자를 입력하세요. 대문자로 자동 변환됩니다. (예: BTC, ETH)' : 'A short abbreviation for your token. Will be automatically converted to uppercase. (e.g., BTC, ETH)'}
              maxLength={10}
              disabled={isLoading || !userWallet}
            /> 
          </div>
          
          
          
          <div className="form-group" style={{display: 'flex', flexDirection: 'row'}}>
            <label htmlFor="tokenSupply" style={{width: '200px', textAlign: 'center'}}>
              {language === 'KR' ? '초기 발행량' : 'Initial Supply'}:  
            </label> 
            <input
                type="number"
                id="tokenSupply"
                value={tokenSupply}
                onChange={(e) => setTokenSupply(e.target.value)}
                min="1"
                disabled={isLoading || !userWallet}
                style={{width: '200px'}}
              />
          </div>

          <div className="form-group" style={{display: 'flex', flexDirection: 'row'}}> 
            <label htmlFor="tokenDecimals" style={{width: '200px', textAlign: 'center'}}> 
              {language === 'KR' ? '소수점 자릿수' : 'Decimals'}: 
            </label> 
            <input
                type="number"
                id="tokenDecimals"
                value={tokenDecimals}
                onChange={(e) => setTokenDecimals(e.target.value)}
                min="0"
                max="9"
                disabled={isLoading || !userWallet} 
              />
          </div>
          
          <div className="form-group" style={{display: 'flex', flexDirection: 'row'}}>
            <label htmlFor="network" style={{width: '200px', textAlign: 'center'}}>
              {language === 'KR' ? '네트워크' : 'Network'}:
            </label>
            <select
              id="network"
              value={network}
              onChange={handleNetworkChange}
              disabled={isLoading || !userWallet}
            >
              <option value="devnet">
                {displayNetworkName('devnet', language)}
              </option>
              <option value="testnet">
                {displayNetworkName('testnet', language)}
              </option>
              <option value="mainnet-beta">
                {displayNetworkName('mainnet-beta', language)}
              </option>
            </select> 
          </div>
          
          {network === 'mainnet-beta' && (
            <div className="network-warning">
              {language === 'KR' 
                ? '⚠️ 메인넷에서는 실제 SOL이 소비됩니다! 토큰 생성 비용이 발생합니다.'
                : '⚠️ Using mainnet will consume real SOL! Token creation costs real funds.'}
            </div>
          )}
          
          <button
            type="submit"
            className="create-token-btn"
            disabled={isLoading || !userWallet}
          >
            {isLoading
              ? (language === 'KR' ? '처리 중...' : 'Processing...')
              : (language === 'KR' ? '토큰 생성하기' : 'Create Token')}
          </button>
        </form>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {result && (
        <div className="result-container">
          <h3 className="step-title">
            {language === 'KR' ? '3단계: 토큰 생성 완료!' : 'Step 3: Token Created!'}
          </h3>
          <p className="step-description">
            {language === 'KR' 
              ? '축하합니다! 토큰이 성공적으로 생성되었습니다. 아래 정보를 확인하고 토큰을 관리하세요.' 
              : 'Congratulations! Your token has been successfully created. Check the details below and manage your token.'}
          </p>
          <div className="result-item">
            <span className="result-label">{language === 'KR' ? '토큰 이름' : 'Token Name'}:</span>
            <span className="result-value">{result.tokenName}</span>
          </div>
          <div className="result-item">
            <span className="result-label">{language === 'KR' ? '토큰 기호' : 'Token Symbol'}:</span>
            <span className="result-value">{result.tokenSymbol}</span>
          </div>
          <div className="result-item">
            <span className="result-label">{language === 'KR' ? '토큰 주소' : 'Token Address'}:</span>
            <div className="result-value-with-copy">
              <span className="result-value">{result.tokenAddress}</span>
              <button 
                className="copy-button small"
                onClick={() => {
                  navigator.clipboard.writeText(result.tokenAddress);
                  alert(language === 'KR' ? '주소가 복사되었습니다!' : 'Address copied!');
                }}
              >
                {language === 'KR' ? '복사' : 'Copy'}
              </button>
            </div>
          </div>
          <div className="result-item">
            <span className="result-label">{language === 'KR' ? '트랜잭션 서명' : 'Transaction Signature'}:</span>
            <a 
              href={`https://explorer.solana.com/tx/${result.signature}?cluster=${network}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="explorer-link"
            >
              {truncateAddress(result.signature, 10, 10)}
            </a>
          </div>
          <div className="explorer-links">
            <a 
              href={`https://explorer.solana.com/address/${result.tokenAddress}?cluster=${network}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="solana-explorer-link"
            >
              {language === 'KR' 
                ? 'Solana Explorer에서 토큰 보기' 
                : 'View token on Solana Explorer'}
            </a>
          </div>
          <div className="result-actions">
            <button
              className="metadata-button"
              onClick={toggleMetadataModal}
            >
              {language === 'KR' ? '메타데이터 등록' : 'Register Metadata'}
            </button>
          </div>
          <div className="next-steps">
            <h4>{language === 'KR' ? '다음 단계' : 'Next Steps'}</h4>
            <ul>
              <li>
                {language === 'KR' 
                  ? '토큰 주소를 복사하여 다른 사람들과 공유하세요.' 
                  : 'Copy your token address to share with others.'}
              </li>
              <li>
                {language === 'KR' 
                  ? 'Phantom, Solflare 등의 지갑 앱에서 토큰을 추가하여 확인할 수 있습니다.' 
                  : 'Add your token to wallet apps like Phantom or Solflare to view it.'}
              </li>
              <li>
                {language === 'KR' 
                  ? '토큰을 다른 지갑으로 전송하려면 Solana 지갑 앱을 사용하세요.' 
                  : 'To transfer tokens to other wallets, use a Solana wallet app.'}
              </li>
            </ul>
          </div>
        </div>
      )}
      
      {transactions.length > 0 && (
        <ListTokens 
          transactions={transactions}
          language={language}
          network={network}
          fetchTransactions={fetchTransactions} // fetchTransactions 함수 전달
        />
      )}

      {/* 지갑 트랜잭션 처리 모달 */}
      {showWalletHandler && pendingTokenData && (
        <div className="wallet-modal-overlay">
          <div className="wallet-modal-container">
            <ExternalWalletTransactionHandler 
              pendingTokenData={pendingTokenData}
              onSuccess={handleWalletSuccess}
              onError={handleWalletError}
              onClose={handleWalletClose}
              language={language}
            />
          </div>
        </div>
      )}
    </div>
  );  
};



export default GenToken;